import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { PhoneNav } from "../../../shared/phone/PhoneNav";
import { Chevron, PnRow } from "../../../shared/phone/Row";
import { Sheet } from "../../../shared/phone/Sheet";
import { usePhone } from "../../../shared/phone/media";
import { Icon } from "../../components/ui/Icon";
import { Inline } from "../../components/ui/Rich";
import { ApiError } from "../../lib/api";
import { libraryApi, type LibraryItem as Item } from "../../lib/libraryApi";
import { lastLoaded, loadKept, prefetchKept } from "../../lib/kept";
import { useToast } from "../goal/useToast";
import type { Ver } from "../versions/Versions";
import { LineSheet } from "./LineSheet";
import { changeOf, leadOf, parseDoc, ruleWins, RULES_ID, type Block, type Doc, type Part, type Spot } from "./parts";
import { bodyOf, verOf } from "./ShelfParts";
import { dayOf, labelOf, placeOf, type ShelfItem } from "./shelf";
import { toolName } from "./tools";
import { reloadShelf, useShelf } from "./useShelf";
import { VersionsSheet, type Pair } from "./VersionsSheet";
import { useCan } from "../../context/AuthContext";

const message = (e: unknown) => (e instanceof ApiError ? e.message : "That did not save. Try again.");
const num = (i: number) => String(i + 1).padStart(2, "0");
const kindWord = (it: ShelfItem) => (it.kind === "skill" ? "Skill" : it.kind === "method" ? "Tool" : "Library");

function Table({ text }: { text: string }) {
  const rows = text
    .split("\n")
    .filter((r) => !/^\s*\|[\s\-:|]+\|\s*$/.test(r))
    .map((r) =>
      r
        .trim()
        .replace(/^\||\|$/g, "")
        .split("|")
        .map((c) => c.trim()),
    );
  const [head, ...body] = rows;
  return (
    <div className="lb-tbl">
      <table>
        <thead>
          <tr>
            {head?.map((c, i) => (
              <th key={i}>
                <Inline text={c} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((r, i) => (
            <tr key={i}>
              {r.map((c, j) => (
                <td key={j}>
                  <Inline text={c} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** One line of a part as a row: tap it to change it. A table shows as it is. */
function Line({ b, lead = false, onTap }: { b: Block; lead?: boolean; onTap?: () => void }) {
  if (b.t === "table")
    return (
      <div className="pn-row static lb-trow">
        <Table text={b.text} />
      </div>
    );
  const cls = ["pn-row", "lb-line", lead ? "lead" : "", b.lvl ? "l1" : "", b.t === "h" ? "h" : ""].filter(Boolean).join(" ");
  const words = (
    <span className="m">
      <span className="lb-lt">
        {b.t === "ol" ? <span className="lb-on">{b.num}.</span> : null}
        <Inline text={b.text} />
      </span>
    </span>
  );
  return onTap ? (
    <button type="button" className={cls} onClick={onTap}>
      {words}
      <Icon name="pen" />
    </button>
  ) : (
    <div className={`${cls} static`}>{words}</div>
  );
}

/**
 * One skill or tool on a phone or an iPad. The kind sits above its name, an update that waits comes first,
 * then the owner's rules and each part as a row; a part opens on its own screen (`?part=`), one line per row,
 * and a line changes in a sheet. Versions open as a sheet from the bar.
 */
/** Where one version's words are kept. */
const itemKey = (versionId: string) => `library-item:${versionId}`;

/** Start loading a library item's words as a finger rests on its row, so it opens ready. */
export function prefetchLibraryItem(versionId: string): void {
  prefetchKept(itemKey(versionId), () => libraryApi.item(versionId).then((r) => r.item));
}

export function ItemNative({ id, part }: { id: string; part: string | null }) {
  const phone = usePhone();
  const navigate = useNavigate();
  const { items, err: shelfErr } = useShelf();
  const [item, setItem] = useState<Item | null>(() => {
    const inUse = items?.find((x) => x.versions.some((v) => v.id === id))?.inUse.id;
    return inUse ? lastLoaded(itemKey(inUse)) : null;
  });
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState<Spot | null>(null);
  const [vers, setVers] = useState<{ start: Pair | null } | null>(null);
  const may = useCan()("editor");
  const [can, setCan] = useState(false);
  const { toast, say } = useToast();
  const root = useRef<HTMLDivElement>(null);
  const itemTop = useRef(0);
  const was = useRef(id);
  const firstRun = useRef(true);

  const it = items?.find((x) => x.versions.some((v) => v.id === id)) ?? null;
  const inUseId = it?.inUse.id ?? null;
  const missing = items !== null && !it;

  // The words in use. Why the id is kept apart: a save shows its version at once, before the shelf catches up.
  const shownId = useRef<string | null>(null);
  shownId.current = item?.id ?? null;
  useEffect(() => {
    if (!inUseId || shownId.current === inUseId) return;
    let alive = true;
    setErr(null);
    loadKept(itemKey(inUseId), () => libraryApi.item(inUseId).then((r) => r.item)).then(
      (r) => alive && setItem(r),
      (e: unknown) => alive && setErr(e instanceof ApiError ? e.message : String(e)),
    );
    return () => {
      alive = false;
    };
  }, [inUseId]);

  // Why: an item and its parts share one address, so the shell keeps the place; a part opens at its top and
  // going back finds the item where it was. On an iPad, a new pick opens at the top of its pane.
  useLayoutEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    if (was.current !== id) {
      was.current = id;
      itemTop.current = 0;
    }
    const sc = root.current?.closest<HTMLElement>(".pn-pane, .pn-main");
    if (sc) sc.scrollTop = part ? 0 : itemTop.current;
  }, [id, part]);

  const openPart = (pid: string) => {
    itemTop.current = root.current?.closest<HTMLElement>(".pn-pane, .pn-main")?.scrollTop ?? 0;
    navigate(`/library/${encodeURIComponent(id)}?part=${encodeURIComponent(pid)}`);
  };

  const fail = err ?? (missing ? "This is not in your library." : !items ? shelfErr : null);
  if (fail || !it || !item || item.kind !== it.kind || item.name !== it.name)
    return (
      <div className="pn-screen" ref={root}>
        <PhoneNav title={it?.title ?? "Library"} back={phone ? { to: "/library", label: "Library" } : undefined} />
        {fail ? (
          <div className="pn-empty">
            <b>This did not load</b>
            {fail}
          </div>
        ) : null}
      </div>
    );

  const doc: Doc = parseDoc(item.body);
  const nextYours = `Yours v${it.versions.filter((v) => v.who === "you").length + 1}`;
  const tools = Array.isArray(item.meta.allowedTools) ? (item.meta.allowedTools as string[]) : [];

  const save = async (value: string) => {
    if (!edit) return;
    const c = changeOf(item.body, doc, edit, value);
    if (!c) return setEdit(null);
    setBusy(true);
    try {
      const { item: saved } = await libraryApi.saveVersion(item.id, { body: c.body, note: c.note });
      setItem(saved);
      setEdit(null);
      say(`Saved as Yours v${saved.version}.`);
      void reloadShelf();
    } catch (e) {
      say(message(e), false);
    } finally {
      setBusy(false);
    }
  };

  const use = async (v: Ver) => {
    setBusy(true);
    try {
      const update = it.waits?.id === v.id;
      const saved = update
        ? (await libraryApi.takeDefault(item.id)).item
        : (await libraryApi.saveVersion(item.id, { body: await bodyOf(v.id), note: `Back to ${v.label}` })).item;
      setItem(saved);
      setVers(null);
      say(update ? `Updated to Flashadz v${v.n}. New goals get it from now.` : `${v.label}'s words are back, as Yours v${saved.version}.`);
      await reloadShelf();
    } catch (e) {
      say(e instanceof ApiError && /Nothing changed/.test(e.message) ? "Those are the words already in use." : message(e), false);
    } finally {
      setBusy(false);
    }
  };

  const keep = async () => {
    if (!it.waits) return;
    const n = it.waits.n;
    setBusy(true);
    try {
      await libraryApi.keepMine(item.id);
      setVers(null);
      say(`Kept ${labelOf(it.inUse).toLowerCase()}. Flashadz v${n} stops waiting.`);
      await reloadShelf();
    } catch (e) {
      say(message(e), false);
    } finally {
      setBusy(false);
    }
  };

  const editing = (() => {
    if (!edit) return null;
    if (edit.part === RULES_ID) return edit.fresh ? { title: "New rule", text: "" } : { title: "Change your rule", text: doc.rules[edit.bi]?.text ?? "" };
    if (edit.fresh) return { title: "New line", text: "" };
    return { title: "Change this line", text: doc.parts.find((p) => p.id === edit.part)?.blocks[edit.bi]?.text ?? "" };
  })();

  const sheets = (
    <>
      {edit && editing ? <LineSheet key={`${edit.part}:${edit.bi}:${edit.fresh ? 1 : 0}`} title={editing.title} initial={editing.text} next={nextYours} busy={busy} onSave={(v) => void save(v)} onClose={() => setEdit(null)} /> : null}
      {vers ? <VersionsSheet it={it} start={vers.start} busy={busy} onUse={(v) => void use(v)} onKeep={() => void keep()} onClose={() => setVers(null)} /> : null}
      <Sheet
        open={can}
        onClose={() => setCan(false)}
        title="What it can do"
        right={
          <button type="button" className="pn-tbtn strong" onClick={() => setCan(false)}>
            Done
          </button>
        }
      >
        <p className="lb-ssub">What the agent may do while it follows {it.title}.</p>
        <div className="pn-grp">
          {tools.map((t) => (
            <PnRow key={t} icon={<Icon name="check" />} iconTone="good" title={toolName(t)} />
          ))}
        </div>
      </Sheet>
      {toast}
    </>
  );

  const shown: Part | null = part ? (doc.parts.find((p) => p.id === part) ?? null) : null;
  if (shown) {
    const pi = doc.parts.indexOf(shown);
    const leadB = doc.written ? null : leadOf(shown);
    const rest = shown.blocks.map((b, bi) => ({ b, bi })).filter(({ b }) => b !== leadB);
    const ri = ruleWins(doc)[shown.id]?.[0];
    return (
      <div className="pn-screen lb-item" ref={root}>
        <PhoneNav title={shown.title} back={{ to: `/library/${encodeURIComponent(id)}`, label: it.title }} large />
        <div className="lb-kind">
          {it.title} · Part {pi + 1} of {doc.parts.length}
        </div>
        <div className="pn-stack">
          <p className="lb-hint">
            <Icon name="pen" />
            Tap a line to change it
          </p>
          {ri !== undefined ? (
            <div className="pn-card lb-win">
              <span className="pn-ico good">
                <Icon name="shield" />
              </span>
              <p>
                <b>Your rule wins here.</b> <Inline text={doc.rules[ri].text} />
              </p>
            </div>
          ) : null}
          {leadB ? (
            <section>
              <div className="pn-cap">In short, line {num(pi)}</div>
              <div className="pn-grp">
                <Line b={leadB} lead onTap={may ? () => setEdit({ part: shown.id, bi: shown.blocks.indexOf(leadB) }) : undefined} />
              </div>
            </section>
          ) : null}
          {shown.blocks.length > 0 ? (
            <div className="pn-grp">
              {rest.map(({ b, bi }) => (
                <Line key={bi} b={b} onTap={may ? () => setEdit({ part: shown.id, bi }) : undefined} />
              ))}
              {may && <PnRow onClick={() => setEdit({ part: shown.id, bi: shown.blocks.length - 1, fresh: true })} icon={<Icon name="plus" />} title="Add a line" className="accent" chevron={false} />}
            </div>
          ) : null}
        </div>
        {sheets}
      </div>
    );
  }

  const mine = it.inUse.who === "you";
  const place = placeOf(it);
  return (
    <div className="pn-screen lb-item" ref={root}>
      <PhoneNav
        title={it.title}
        back={phone ? { to: "/library", label: "Library" } : undefined}
        large
        sub={it.line || undefined}
        actions={
          <button type="button" className="pn-tbtn lb-tw" onClick={() => setVers({ start: null })}>
            Versions
          </button>
        }
      />
      <div className="lb-kind">{place === "Other" ? kindWord(it) : `${kindWord(it)} · ${place}`}</div>
      <div className="lb-since">
        <span className={mine ? "pn-pill good" : "pn-pill acc"}>{labelOf(it.inUse)}</span>
        <span>Since {dayOf(it.inUse.at)}</span>
      </div>
      <div className="pn-stack">
        {it.waits ? (
          <div className="pn-card lb-upd">
            <div className="lb-upd-h">
              <span className="pn-ico warn">
                <Icon name="lbSparkle" />
              </span>
              <div>
                <h3>Flashadz v{it.waits.n} is out</h3>
                {it.waits.note ? <p>{it.waits.note}</p> : null}
              </div>
            </div>
            <button type="button" className="lb-upd-l" onClick={() => it.waits && setVers({ start: { from: verOf(it.inUse), to: verOf(it.waits) } })}>
              <span>What changed</span>
              <Chevron />
            </button>
            {may && (
              <div className="pn-btns">
                <button type="button" className="pn-btn sm" disabled={busy} onClick={() => void keep()}>
                  Keep mine
                </button>
                <button type="button" className="pn-btn sm pri" disabled={busy} onClick={() => it.waits && void use(verOf(it.waits))}>
                  Update
                </button>
              </div>
            )}
          </div>
        ) : null}
        {(may || doc.rules.length > 0) && (
          <section>
            <div className="pn-cap">Your rules</div>
            <div className="pn-grp">
              {doc.rules.map((r, ri) => (
                <PnRow key={ri} onClick={may ? () => setEdit({ part: RULES_ID, bi: ri }) : undefined} icon={<Icon name="shield" />} iconTone="good" title={<Inline text={r.text} />} className="lb-rule" chevron={false} />
              ))}
              {may && <PnRow onClick={() => setEdit({ part: RULES_ID, bi: doc.rules.length - 1, fresh: true })} icon={<Icon name="plus" />} title="Add a rule" className="accent" chevron={false} />}
            </div>
            <p className="pn-note">In your words. They win over anything below.</p>
          </section>
        )}
        <section>
          <div className="pn-cap">
            {doc.parts.length} {doc.parts.length === 1 ? "part" : "parts"}
          </div>
          <div className="pn-grp">
            {doc.parts.map((p, i) => {
              const lead = leadOf(p)?.text ?? p.blocks[0]?.text ?? "";
              return (
                <button type="button" className="pn-row lb-prow" key={p.id} onClick={() => openPart(p.id)}>
                  <span className="lb-pn">{num(i)}</span>
                  <span className="m">
                    <span className="t">{p.title}</span>
                    {lead ? (
                      <span className="s">
                        <Inline text={lead} />
                      </span>
                    ) : null}
                  </span>
                  <Chevron />
                </button>
              );
            })}
          </div>
        </section>
        {tools.length > 0 ? (
          <div className="pn-grp">
            <PnRow onClick={() => setCan(true)} icon={<Icon name="tool" />} iconTone="plain" title="What it can do" value={String(tools.length)} chevron />
          </div>
        ) : null}
      </div>
      {sheets}
    </div>
  );
}
